import { useCallback, useEffect, useState } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { SectionTitle } from "features/SectionTitle";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { useNotifyError } from "utils/useNotifyError";
import PriceSection from "./PriceSection";
import ElectiveGroupRow from "./ElectiveGroupRow";

const DisciplinePricesPage = ({ academicYearId, specialityId }) => {
  const notifyError = useNotifyError();
  const [prices, setPrices] = useState(null);

  const fetchPrices = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getCurriculumDisciplinePrices(academicYearId, specialityId),
        null,
        "GET",
      );
      setPrices(data ?? null);
    } catch (err) {
      notifyError(err);
      setPrices(null);
    }
  }, [academicYearId, specialityId, notifyError]);

  useEffect(() => {
    if (!academicYearId || !specialityId) return;
    fetchPrices();
  }, [academicYearId, specialityId, fetchPrices]);

  if (!prices) return null;

  const { curriculumId } = prices;

  return (
    <Box sx={{ width: "100%", overflow: "hidden", pb: 3 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography>
          Учебен план: <strong>{prices.curriculumName ?? "-"}</strong>
        </Typography>
      </Paper>

      <PriceSection
        title="Задължителни дисциплини"
        periodLabel="Семестър"
        periods={prices.regularSemesters}
        buildUrl={row =>
          studentRequests.saveRegularDisciplinePrice(curriculumId, academicYearId, row.disciplineId, row.period)
        }
        onSaved={fetchPrices}
      />

      <PriceSection
        title="Факултативни дисциплини"
        periodLabel="Семестър"
        periods={prices.facultativeSemesters}
        buildUrl={row =>
          studentRequests.saveFacultativeDisciplinePrice(curriculumId, academicYearId, row.disciplineId, row.period)
        }
        onSaved={fetchPrices}
      />

      <PriceSection
        title="Циклични дисциплини"
        periodLabel="Курс"
        periods={prices.cyclicYears}
        buildUrl={row =>
          studentRequests.saveCyclicDisciplinePrice(curriculumId, academicYearId, row.disciplineId, row.period)
        }
        onSaved={fetchPrices}
      />

      {prices.electiveGroups?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle
            title="Избираеми групи"
            paddingY={1}
          />
          <Grid
            container
            spacing={1}
            sx={{ display: { xs: "none", md: "flex" }, mb: 1 }}
          >
            {[
              { label: "Група", md: 6 },
              { label: "Националност", md: 2 },
              { label: "Цена (EUR)", md: 2 },
              { label: "Действия", md: 2 },
            ].map(header => (
              <Grid
                item
                key={header.label}
                md={header.md}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold" }}
                >
                  {header.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
          {prices.electiveGroups.map(group => (
            <ElectiveGroupRow
              key={group.groupId}
              group={group}
              curriculumId={curriculumId}
              academicYearId={academicYearId}
              onSaved={fetchPrices}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DisciplinePricesPage;
